import React, { useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button, Grid, Icon, Text, theme } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'
import { AvailableModules } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import * as styles from 'modules/marketplace_modules/components/Contact/index.vanilla.css'
import useCheckModuleOnTrial from 'modules/marketplace_modules/hooks/useCheckModuleOnTrial'
import useCreateSubscriptionOverload from 'modules/marketplace_modules/hooks/useCreateSubscriptionOverload'
import useSubscriptionOverloadEvent from 'modules/marketplace_modules/hooks/useSubscriptionOverloadEvent'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import ShareLinkModal from '../ShareLinkModal'
import TrialModal from '../TrialModal'

type CTAProps = {
  handleSubscriptionOverload: () => void
  isAcquired: boolean
  isOnTrial: boolean
  chargebeeModuleName?: AvailableModules
  chargebeeId?: string
  upsellOrigin: string
  setIsTrialModalOpen: (isTrialModalOpen: boolean) => void
}

const ModuleCTA = ({
  handleSubscriptionOverload,
  isAcquired,
  isOnTrial,
  chargebeeModuleName,
  chargebeeId,
  upsellOrigin,
  setIsTrialModalOpen,
}: CTAProps) => {
  const { company } = useCurrentSession()
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const inFreeTrial = company.subscription.inFreeTrial
  const buttonType = isAcquired || isOnTrial ? 'secondary' : 'upgrade'
  const buttonText =
    isAcquired || isOnTrial
      ? i18n.t('marketplace.modules.uninstall_module')
      : i18n.t('marketplace.modules.try_module')

  if (!chargebeeModuleName) return null

  if (ignoreUpsell || inFreeTrial) {
    if (!chargebeeId) return null
    return (
      <Button type={buttonType} onClick={handleSubscriptionOverload}>
        {buttonText}
      </Button>
    )
  }

  return (
    <>
      {!isAcquired && (
        <Box flexDirection="row" gap="s8" flexWrap="wrap" justifyContent="flexEnd">
          <UpsellingKit.UpsellButton
            moduleRequested={chargebeeModuleName}
            requestSource={upsellOrigin}
            requestReason={ValidReasons.MissingAddon}
            pageSource={upsellOrigin}
          />
          {chargebeeId && (
            <Button type="upgrade" onClick={() => setIsTrialModalOpen(true)}>
              {i18n.t('marketplace.modules.trial_upsell.button')}
            </Button>
          )}
        </Box>
      )}
    </>
  )
}

type ContactProps = {
  data: {
    icon?: React.ComponentProps<typeof Icon.Large>['icon']
    image?: { url: string; alt: string; logoBackground?: string }
    title: string
    description: string
    chargebeeModuleId?: string
    chargebeeModuleName?: AvailableModules
  }
  moduleId: string
  isAcquired?: boolean
}

const Contact = ({ data, moduleId, isAcquired }: ContactProps) => {
  const { company: currentCompany } = useCurrentSession()
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const { icon, image, title, description } = data
  const { isModuleOnTrial } = useCheckModuleOnTrial()
  const isOnTrial = isModuleOnTrial(data.chargebeeModuleId)
  const { navigation } = useNavigation()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isPopularModule = queryParams.get('pm') === 'true'
  const upsellOrigin = queryParams.get('upsell_origin')
  const initUpsell = queryParams.get('init_upsell') === 'true'

  const createSubscriptionOverload = useCreateSubscriptionOverload([currentCompany.id])
  const { eventEmitter } = useSubscriptionOverloadEvent({
    moduleName: moduleId,
    chargebeeId: data.chargebeeModuleId || '',
    isPopularModule,
  })

  const handleSubscriptionOverload = async () => {
    if (!data.chargebeeModuleId) return
    const overloadReason = isOnTrial ? 'disable_module' : 'enable_module'

    await createSubscriptionOverload({
      chargebeeId: data.chargebeeModuleId,
      overloadReason,
    })

    eventEmitter(
      isOnTrial
        ? trackingEvents.marketplaceModules.disableModule
        : trackingEvents.marketplaceModules.enableModule
    )
    window.location.href = navigation.discover.marketplaceModules.fullPath()
  }

  const shareLink = () => {
    setIsShareModalOpen((prev) => !prev)
  }

  const getLogoBackground = () => {
    if (image !== undefined) {
      if (image.logoBackground !== undefined && image.logoBackground !== '') {
        return image.logoBackground
      } else {
        return theme.colors.grey000White
      }
    } else {
      return theme.colors.grey200
    }
  }

  return (
    <>
      <Box
        width="full"
        paddingY="s24"
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        borderRadius={{ all: 'abs012' }}
        background="grey100"
        paddingLeft="s24"
        paddingRight="s32"
        flexDirection="column"
        alignItems="stretch"
      >
        <Grid
          variant="fixed"
          style={{ rowGap: theme.dimensions.s12 }}
          className={styles.contactTitleWrapper}
        >
          <Box
            flexDirection="row"
            gap="s24"
            alignItems="center"
            gridColSpan={{ xxs: 12, s: 7 }}
            className={styles.contactTitle}
          >
            <div className={styles.logo} style={{ backgroundColor: getLogoBackground() }}>
              {icon !== undefined && <Icon.Large icon={icon} color="grey800" />}
              {icon === undefined && image !== undefined ? (
                <img src={image.url} alt={image.alt} />
              ) : null}
            </div>
            <Box maxWidth="s600">
              <Text size="500" weight="semibold" color="grey1000">
                {title}
              </Text>
              <Text.Normal>{description}</Text.Normal>
            </Box>
          </Box>
          <Box
            flexDirection="row"
            gap="s8"
            flexWrap="wrap"
            justifyContent="flexEnd"
            alignItems="center"
            alignSelf="center"
            gridColSpan={{ xxs: 12, s: 5 }}
          >
            {isAcquired && <Button type="secondary" onClick={shareLink} iconName="link" />}
            <ModuleCTA
              handleSubscriptionOverload={handleSubscriptionOverload}
              isAcquired={isAcquired || false}
              isOnTrial={isOnTrial}
              chargebeeId={data.chargebeeModuleId}
              chargebeeModuleName={data.chargebeeModuleName}
              upsellOrigin={`From marketplace ${data.chargebeeModuleName}${
                upsellOrigin ? ` - ${upsellOrigin}` : ''
              }`}
              setIsTrialModalOpen={setIsTrialModalOpen}
            />
          </Box>
        </Grid>
      </Box>
      {(isUpsellModalOpen || initUpsell) && data.chargebeeModuleName && (
        <RequestUpgradeModal
          moduleRequested={data.chargebeeModuleName}
          requestReason={ValidReasons.MissingAddon}
          requestSource={`From marketplace ${data.chargebeeModuleName}${
            upsellOrigin ? ` - ${upsellOrigin}` : ''
          }`}
          onClose={() => setIsUpsellModalOpen(false)}
        />
      )}
      {isShareModalOpen && (
        <ShareLinkModal
          moduleTitle={data.title}
          moduleUrl={window?.location?.href}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
      {isTrialModalOpen && data.chargebeeModuleName && (
        <TrialModal
          moduleData={{
            icon: data.icon,
            chargebeeModuleId: data.chargebeeModuleId || '',
          }}
          requestDetail={{
            moduleRequested: data.chargebeeModuleName,
            requestSource: `From marketplace trial ${data.chargebeeModuleName}${
              upsellOrigin ? ` - ${upsellOrigin}` : ''
            }`,
            requestReason: ValidReasons.MissingAddon,
          }}
          onClose={() => setIsTrialModalOpen(false)}
        />
      )}
    </>
  )
}

export default Contact
